import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, ApiError, types } from "../api/index.js";
import { apiLink } from "../utils/format.js";
import { displayCanonicalWithTags } from "../utils/canonical.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";

type SeverityFilter = "all" | "Critical" | "High" | "Medium" | "Low" | "Negligible" | "Unknown";

@customElement("vulnerabilities-tab")
export class VulnerabilitiesTab extends LitElement {
  @property() imageName = "";
  @property() canonical = "";
  @property({ type: Array }) canonicals: string[] = [];
  @property({ type: Array }) tags: types.Tag[] = [];
  @state() private _severity: SeverityFilter = "all";
  @state() private _filter = "";

  createRenderRoot() {
    return this;
  }

  private _task = new Task(this, {
    args: () => [this.imageName, this.canonical] as const,
    task: ([name, canonical]) =>
      canonical
        ? fetchApi<types.VulnerabilitiesResponse>(`/v1/images/${name}/vulnerabilities/${canonical}`)
        : Promise.reject("No canonical"),
  });

  private _onCanonicalChange(e: Event) {
    this.dispatchEvent(
      new CustomEvent("canonical-change", { detail: (e.target as HTMLSelectElement).value }),
    );
  }

  private static _severityOrder: Record<string, number> = {
    Critical: 0,
    High: 1,
    Medium: 2,
    Low: 3,
    Negligible: 4,
    Unknown: 5,
  };

  private static _componentNames(v: types.VulnerabilityFinding): string[] {
    return v.components.map((c) => c.name);
  }

  private _filtered(vulns: types.VulnerabilityFinding[]): types.VulnerabilityFinding[] {
    let filtered = vulns;
    if (this._severity !== "all") {
      filtered = filtered.filter((v) => v.severity === this._severity);
    }
    if (this._filter) {
      const q = this._filter.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.id.toLowerCase().includes(q) ||
          VulnerabilitiesTab._componentNames(v).some((c) => c.toLowerCase().includes(q)) ||
          v.description.toLowerCase().includes(q),
      );
    }
    return filtered.toSorted(
      (a, b) =>
        (VulnerabilitiesTab._severityOrder[a.severity] ?? 99) -
        (VulnerabilitiesTab._severityOrder[b.severity] ?? 99),
    );
  }

  render() {
    return html`
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <div>
          <label class="text-gray-500 text-sm mr-1">Tag:</label>
          <select
            .value=${this.canonical}
            @change=${this._onCanonicalChange}
            class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
          >
            ${this.canonicals.map(
              (c) =>
                html`<option value=${c} ?selected=${c === this.canonical}>
                  ${displayCanonicalWithTags(c, this.tags)}
                </option>`,
            )}
          </select>
        </div>
        <div class="ml-auto">
          ${this.canonical ? apiLink(`/v1/images/${this.imageName}/vulnerabilities/${this.canonical}`) : ""}
        </div>
      </div>

      ${this._task.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) =>
          e instanceof ApiError && e.status === 404
            ? html`<p class="text-gray-500 text-sm py-8 text-center">
                No vulnerability scan data available for this release yet.
              </p>`
            : html`<error-message .message=${String(e)}></error-message>`,
        complete: (data) => this._renderData(data),
      })}
    `;
  }

  private static _formatTimestamp(raw: string): string {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
  }

  private _renderSummaryBar(summary: types.VulnerabilitiesSummary) {
    const items: { label: string; count: number; color: string }[] = [
      { label: "Critical", count: summary.critical, color: "bg-red-500" },
      { label: "High", count: summary.high, color: "bg-orange-400" },
      { label: "Medium", count: summary.medium, color: "bg-yellow-400" },
      { label: "Low", count: summary.low, color: "bg-sky-400" },
      { label: "Negligible", count: summary.negligible, color: "bg-gray-400" },
      { label: "Unknown", count: summary.unknown, color: "bg-gray-500" },
    ];
    return html`
      <div class="flex gap-3 mb-4 flex-wrap">
        ${items.map(
          ({ label, count, color }) => html`
            <button
              @click=${() => (this._severity = this._severity === (label as SeverityFilter) ? "all" : (label as SeverityFilter))}
              class="flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition-colors
                     ${this._severity === label
                       ? "ring-1 ring-hb-accent bg-hb-surface"
                       : "hover:bg-hb-surface/50"}"
            >
              <span class="w-2.5 h-2.5 rounded-full ${color}"></span>
              <span class="text-gray-700 dark:text-gray-300">${label}</span>
              <span class="text-gray-500 dark:text-gray-400 font-mono">${count}</span>
            </button>
          `,
        )}
      </div>
    `;
  }

  private _renderToggle(data: types.VulnerabilitiesResponse) {
    return html`
      <div class="flex items-center gap-4 mb-3">
        <span class="text-gray-600 dark:text-gray-400 text-sm">
          ${data.summary.total} vulnerabilities
        </span>
        <span class="text-gray-600 dark:text-gray-500 text-xs">
          Scanned ${VulnerabilitiesTab._formatTimestamp(data.scanned_at)}
          · ${data.scanner}
        </span>
      </div>
    `;
  }

  private _severityBadge(severity: string) {
    const colors: Record<string, string> = {
      Critical: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-400 dark:border-red-700/50",
      High: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-400 dark:border-orange-700/50",
      Medium: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-700/50",
      Low: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-blue-900/50 dark:text-blue-400 dark:border-blue-700/50",
      Negligible: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600/50",
      Unknown: "bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800/50 dark:text-gray-500 dark:border-gray-700/50",
    };
    const cls = colors[severity] ?? "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-600/50";
    return html`<span
      class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${cls}"
      >${severity}</span
    >`;
  }

  private _renderData(data: types.VulnerabilitiesResponse) {
    if (data.summary.total === 0) {
      return html`
        ${this._renderToggle(data)}
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <div class="text-3xl mb-3">✅</div>
          <h3 class="text-gray-300 text-lg font-bold mb-1">No vulnerabilities found</h3>
          <p class="text-gray-500 text-sm">This release has no known CVEs.</p>
        </div>
      `;
    }

    const filtered = this._filtered(data.vulnerabilities);

    return html`
      ${this._renderToggle(data)} ${this._renderSummaryBar(data.summary)}

      <div class="flex items-center justify-between mb-3">
        <span class="text-gray-500 text-xs">
          ${this._severity !== "all" || this._filter
            ? `Showing ${filtered.length} of ${data.summary.total}`
            : ""}
        </span>
        <input
          type="text"
          placeholder="Filter by CVE, component..."
          .value=${this._filter}
          @input=${(e: Event) => (this._filter = (e.target as HTMLInputElement).value)}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-xs text-gray-200
                 placeholder:text-gray-500 focus:outline-none focus:border-hb-accent w-56"
        />
      </div>

      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3 w-20">Severity</th>
              <th class="py-2 px-3 w-40">CVE</th>
              <th class="py-2 px-3 w-44">Components</th>
              <th class="py-2 px-3">Description</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(
              (v) => html`
                <tr class="border-b border-hb-border/50 hover:bg-hb-surface/30">
                  <td class="py-1.5 px-3">${this._severityBadge(v.severity)}</td>
                  <td class="py-1.5 px-3 font-mono">
                    ${v.url
                      ? html`<a
                          href=${v.url}
                          target="_blank"
                          rel="noopener"
                          class="text-hb-accent hover:underline"
                          >${v.id}</a
                        >`
                      : html`<span class="text-gray-200">${v.id}</span>`}
                  </td>
                  <td class="py-1.5 px-3 text-gray-200 font-mono">${VulnerabilitiesTab._componentNames(v).join(", ")}</td>
                  <td class="py-1.5 px-3 text-gray-400 truncate max-w-md">${v.description}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}
